import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Loader,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
} from "react-feather";
import { GOOGLE_MAPS_CONFIG } from "../../config/keys";
import { postData } from "../../utility/Utility";

const LocationUpdate = ({ show, onClose, onConfirm, initialLocation = {} }) => {
  const navigate = useNavigate();
  // Extract location from initialLocation (which now contains the full user object)
  const userLocation = initialLocation.location || {};
  const [location, setLocation] = useState({
    address: userLocation.address || "",
    city: userLocation.city || "",
    state: userLocation.state || "",
    pincode: userLocation.pincode || "",
    latitude: userLocation.latitude || null,
    longitude: userLocation.longitude || null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const searchInputRef = useRef(null);

  // Load Google Places API
  useEffect(() => {
    if (!window.google || !window.google.maps) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_CONFIG.apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google && window.google.maps) {
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
          placesService.current = new window.google.maps.places.PlacesService(
            document.createElement("div")
          );
        }
      };
      document.head.appendChild(script);
    } else {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
    }
  }, []);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 2) {
      setIsLoading(true);
      autocompleteService.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: "in" },
          types: ["geocode"],
        },
        (predictions, status) => {
          setIsLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions || []);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle place selection
  const handleSelectPlace = (placeId) => {
    if (!placeId) return;

    setIsLoading(true);
    const request = {
      placeId,
      fields: [
        "address_components",
        "geometry",
        "formatted_address",
        "formatted_phone_number",
      ],
    };

    placesService.current.getDetails(request, (place, status) => {
      setIsLoading(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const newLocation = {
          address: "",
          city: "",
          state: "",
          pincode: "",
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
        };

        // Process address components
        place.address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("street_number") || types.includes("route")) {
            newLocation.address =
              component.long_name +
              (newLocation.address ? ", " + newLocation.address : "");
          } else if (
            types.includes("sublocality_level_1") ||
            types.includes("sublocality")
          ) {
            newLocation.address =
              component.long_name +
              (newLocation.address ? ", " + newLocation.address : "");
          } else if (types.includes("locality")) {
            newLocation.city = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            newLocation.state = component.long_name;
          } else if (types.includes("postal_code")) {
            newLocation.pincode = component.long_name;
          }
        });

        // If pincode is still empty, try to extract it from formatted address as fallback
        if (!newLocation.pincode) {
          const pincodeMatch = place.formatted_address.match(/\b\d{6}\b/);
          if (pincodeMatch) {
            newLocation.pincode = pincodeMatch[0];
          }
        }

        setLocation(newLocation);
        setSearchQuery(place.formatted_address);
        setShowSuggestions(false);
      }
    });
  };

  // Handle form submission directly
  const handleSubmitClick = () => {
    handleSubmit();
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      // Validate pincode is provided and is 6 digits
      if (!location.pincode || !/^\d{6}$/.test(location.pincode)) {
        throw new Error("Please enter a valid 6-digit pincode");
      }

      const payload = {
        userId: initialLocation._id,
        email: initialLocation.email,
        targetRole: initialLocation.role,
        address: location.address || "",
        city: location.city || "",
        state: location.state || "",
        pincode: location.pincode,
        ...(location.latitude && { latitude: location.latitude }),
        ...(location.longitude && { longitude: location.longitude }),
        formattedAddress: `${location.address || ""}${
          location.city ? ", " + location.city : ""
        }${location.state ? ", " + location.state : ""} ${
          location.pincode || ""
        }`.trim(),
      };

      // Make API call to update location
      const response = await postData("/api/users/update-location", payload);

      if (response.success) {
        onClose();
        navigate("/user-management", { replace: true });
        if (onConfirm) {
          onConfirm({ location: payload });
        }
      } else {
        throw new Error(response.message || "Failed to update location");
      }
    } catch (err) {
      console.error("Error updating location:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to update location";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!show) return null;

  return (
    <div
      className="modal show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.5)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title d-flex align-items-center">
              <i className="mdi mdi-map-marker-edit me-2"></i>
              Update Location
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {error && (
              <div
                className="alert alert-danger d-flex align-items-center"
                role="alert"
              >
                <AlertCircle className="flex-shrink-0 me-2" size={18} />
                <div>{error}</div>
              </div>
            )}

            {success && (
              <div
                className="alert alert-success d-flex align-items-center"
                role="alert"
              >
                <CheckCircle className="flex-shrink-0 me-2" size={18} />
                <div>{success}</div>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">Search Location</label>
              <div className="position-relative" ref={searchInputRef}>
                <div className="input-group">
                  <span className="input-group-text">
                    <MapPin size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search for a location..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    disabled={isSubmitting}
                  />
                  {isLoading && (
                    <span className="position-absolute end-0 top-50 translate-middle-y me-3">
                      <Loader size={18} className="text-muted" />
                    </span>
                  )}
                </div>

                {showSuggestions && suggestions.length > 0 && (
                  <div className="list-group position-absolute w-100 zindex-dropdown">
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        className="list-group-item list-group-item-action text-start"
                        onClick={() => handleSelectPlace(suggestion.place_id)}
                      >
                        {suggestion.description}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={location.address || ""}
                  onChange={(e) =>
                    setLocation({ ...location, address: e.target.value })
                  }
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={location.city || ""}
                  onChange={(e) =>
                    setLocation({ ...location, city: e.target.value })
                  }
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-control"
                  value={location.state || ""}
                  onChange={(e) =>
                    setLocation({ ...location, state: e.target.value })
                  }
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Pincode <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    error && !location.pincode ? "is-invalid" : ""
                  }`}
                  value={location.pincode || ""}
                  onChange={(e) => {
                    // Only allow numbers and limit to 6 digits
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setLocation({ ...location, pincode: value });
                    if (error) setError("");
                  }}
                  disabled={isSubmitting}
                  required
                  placeholder="Enter 6-digit pincode"
                />
                <div className="invalid-feedback">
                  Please enter a valid 6-digit pincode
                </div>
              </div>
            </div>

            {location.address && (
              <div className="mt-3 p-3 bg-light rounded">
                <h6>Selected Location:</h6>
                <p className="mb-0">
                  {location.address}, {location.city}, {location.state}{" "}
                  {location.pincode}
                </p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmitClick}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="mdi mdi-map-marker-check me-1"></i>
                  Update Location
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationUpdate;
